/**
 * Payroll / „secure area” wg AuthX: backend zwraca Sec2YN (np. w verify-mfa, refresh-key).
 */

import envConfig from "../config";
import { getCookie, setCrossDomainCookie } from "./cookie-utils";
import { isAuthxAppsSuccessBody } from "./authx-apps-response";

export const COOKIE_AUTHX_SEC2_YN = "authxSec2YN";

const REFRESH_ENDPOINT = () => `https://${envConfig.apiAuthx}/api/apps/refresh-key`;

/** Odczyt Sec2YN / sec2YN z payloadu AuthX (camel lub PascalCase). */
export function normalizeAuthxSec2Yn(data) {
  if (!data || typeof data !== "object") return null;
  const d = data;
  const v = d.sec2YN ?? d.Sec2YN ?? d.sec2Yn;
  if (v === true) return "Y";
  if (v === false) return "N";
  if (typeof v === "string") {
    const u = v.trim().toUpperCase();
    if (u === "Y") return "Y";
    if (u === "N") return "N";
  }
  return null;
}

function formatExpiryForCookie(expiryUtc) {
  if (!expiryUtc || typeof expiryUtc !== "string") return null;
  return expiryUtc.endsWith("Z") ? expiryUtc.slice(0, -1) : expiryUtc;
}

/** Spójny format dat AuthX dla starych cookies OLIV (bez „Z”; spacja→T jak w LoginPopup). */
function formatUtcForLegacyCookie(raw) {
  if (raw == null) return null;
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s) return null;
  const withT = s.replace(" ", "T");
  return withT.endsWith("Z") ? withT.slice(0, -1) : withT;
}

/** Zapis Sec2YN + opcjonalna rotacja kluczy sesji po refresh-key / verify-mfa. */
export function persistAuthxSec2AndKeysFromPayload(data) {
  if (!data || typeof data !== "object") return null;
  const d = data;
  const yn = normalizeAuthxSec2Yn(d);
  if (yn !== null) {
    setCrossDomainCookie(COOKIE_AUTHX_SEC2_YN, yn);
  }

  const ak = d.accessKey ?? d.AccessKey;
  const rk = d.refreshKey ?? d.RefreshKey;
  if (typeof ak === "string" && ak) {
    setCrossDomainCookie("accessKey", ak);
  }
  if (typeof rk === "string" && rk) {
    setCrossDomainCookie("refreshKey", rk);
  }
  const exp = formatExpiryForCookie(d.expiryUtc ?? d.ExpiryUtc);
  if (exp) {
    setCrossDomainCookie("apiKeyExpirationUTCDate", exp);
  }

  const lastPwd = formatUtcForLegacyCookie(d.lastPwdUtc ?? d.LastPwdUtc);
  if (lastPwd) {
    setCrossDomainCookie("loginLastPwdUTCDate", lastPwd);
  }
  const lastMfa = formatUtcForLegacyCookie(d.lastMfaUtc ?? d.LastMfaUtc);
  if (lastMfa) {
    setCrossDomainCookie("login2FaCodeLastUsedUTCDate", lastMfa);
  }
  const last2Sec = formatUtcForLegacyCookie(
    d.last2SecAccessUtc ?? d.Last2SecAccessUtc,
  );
  if (last2Sec) {
    setCrossDomainCookie("loginLastSec2CheckInUTCDate", last2Sec);
  }

  const xxu =
    d.xxuID ??
    d.XxuID ??
    d.globalUserId ??
    d.GlobalUserId ??
    d.GlobalUserID;
  if (xxu !== undefined && xxu !== null && xxu !== "") {
    setCrossDomainCookie("globalUserId", String(xxu));
  }

  return yn;
}

/** Czy użytkownik ma potwierdzone MFA wg ostatniej znanej odpowiedzi backendu (Sec2YN === Y). */
export function isPayrollSec2Confirmed() {
  return getCookie(COOKIE_AUTHX_SEC2_YN) === "Y";
}

/** POST `/api/apps/refresh-key` — aktualny Sec2YN + ewentualna rotacja kluczy. */
export async function fetchAuthxRefreshKeyAndPersistSec2() {
  const accessKey = getCookie("accessKey");
  const refreshKey = getCookie("refreshKey");
  // olivs-login zawsze wysyła "0" jako database-id.
  const databaseId = "0";

  if (
    !accessKey ||
    accessKey === "undefined" ||
    !refreshKey ||
    refreshKey === "undefined"
  ) {
    return false;
  }

  try {
    const res = await fetch(REFRESH_ENDPOINT(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/plain",
        "browser-agent":
          typeof window !== "undefined" ? window.navigator.userAgent : "",
        "database-id": databaseId,
        "olivs-api-real-ip": "127.0.0.1",
        "olivs-api-computer-name": "BTM-WS2019",
        "access-key": accessKey,
        "refresh-key": refreshKey,
        "x-skip-auth-interceptor": "true",
      },
      body: JSON.stringify({
        accessKey,
        refreshKey,
      }),
    });

    const data = await res.json().catch(() => null);
    if (!data || typeof data !== "object") return false;

    persistAuthxSec2AndKeysFromPayload(data);
    return res.ok || isAuthxAppsSuccessBody(data);
  } catch {
    return false;
  }
}
